import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import TeamIcon from "@components/icons/TeamIcon";
import { EventContentSkeleton } from "@features/offer/desktop/event";
// @ts-ignore
import { drawField } from "@features/offer/shared/statistics/components/team-lineups";
import TeamLineupsStore from "@state/stores/offer/statistics/TeamLineupsStore";
import { observer } from "mobx-react";

import TeamLineupsPlayerInfo from "./TeamLineupsPlayerInfo";
import TeamLineupsBench from "./TeamLineupsBench";

const MOBILE_SPACING: { [key: string]: number } = {
	"5": 40,
	"4": 40,
	"3": 65,
	"2": 105,
	default: 0,
};

export default observer(function TeamLineups(props: {
	store: TeamLineupsStore;
}) {
	const fieldRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [fieldDimensions, setFieldDimensions] = useState<{
		fieldWidth: number;
		fieldHeight: number;
	}>({ fieldWidth: 0, fieldHeight: 880 });
	const { t } = useTranslation();

	useEffect(() => {
		const updateFieldDimensions = () => {
			if (containerRef.current) {
				setFieldDimensions({
					fieldWidth: containerRef.current.offsetWidth,
					fieldHeight: fieldDimensions.fieldHeight,
				});
			}
		};

		updateFieldDimensions();
		window.addEventListener("resize", updateFieldDimensions);

		return () =>
			window.removeEventListener("resize", updateFieldDimensions);
	}, []);

	useEffect(() => {
		if (!fieldRef.current || !props.store.teamLineups) return;

		const pitch = fieldRef.current.getContext("2d");
		if (!pitch) return;

		fieldRef.current.width = fieldDimensions.fieldWidth;
		fieldRef.current.height = fieldDimensions.fieldHeight;
		drawField(
			pitch,
			fieldDimensions.fieldWidth,
			fieldDimensions.fieldHeight
		);
	}, [props.store.teamLineups, fieldDimensions]);

	if (props.store.loader.isLoading) {
		return <EventContentSkeleton />;
	}

	if (
		!props.store.teamLineups?.homeFormation ||
		!props.store.teamLineups?.homeTeamPlayers ||
		!props.store.teamLineups?.awayFormation ||
		!props.store.teamLineups?.awayTeamPlayers
	) {
		return null;
	}

	const homePositions = props.store.createTeamLineupsPositions(
		props.store.teamLineups?.homeFormation,
		props.store.teamLineups?.homeTeamPlayers.length,
		true,
		fieldDimensions,
		MOBILE_SPACING
	);

	const awayPositions = props.store.createTeamLineupsPositions(
		props.store.teamLineups?.awayFormation,
		props.store.teamLineups?.awayTeamPlayers.length,
		false,
		fieldDimensions,
		MOBILE_SPACING
	);

	return (
		<>
			<div className="card card--raised card--stats">
				{!props.store.isLiveOrHistory ? (
					props.store.teamLineups?.lineupConfirmed ? (
						<div className="card__header card--stats__header">
							<div className="card__title card__title--base card--stats__heading">
								{t(
									"EVENT.STATISTICS.LINEUPS.CONFIRMED_LINEUPS"
								)}
							</div>
						</div>
					) : (
						<div className="card__header card--stats__header">
							<div className="card__title card__title--base card--stats__heading">
								{t("EVENT.STATISTICS.LINEUPS.POSSIBLE_LINEUPS")}
							</div>
						</div>
					)
				) : null}

				<div className="card--stats__body">
					<div className="card--stats__row u-display--f--align-center">
						<div className="card--stats__col card--stats__col--left card--stats__col--grow">
							<div className="card--stats__subtitle u-type--sml">
								{
									props.store.playerStatisticsStore.eventStore
										.teamOne?.name
								}
							</div>
						</div>
						<div className="card--stats__col card--stats__col--right u-display--f--align-center">
							<div className="card--stats__value card--stats__value--fit u-type--sml">
								{props.store.teamLineups?.homeFormation}
							</div>
							<TeamIcon
								showCountryFlag={
									props.store.playerStatisticsStore.eventStore
										.tournament?.showCountryFlag
								}
								flagCode={
									props.store.playerStatisticsStore.eventStore
										.teamOne?.countryCode
								}
								jerseyTypeAbrv={
									props.store.playerStatisticsStore.eventStore
										.teamOne?.jerseyTypeAbrv
								}
								opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamTwo?.countryCode}
								opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamTwo?.jerseyTypeAbrv}
								className="u-icon u-icon--med u-mar--left--3"
							/>
						</div>
					</div>

					<div
						ref={containerRef}
						className="card--field"
						style={{
							height: `${fieldDimensions.fieldHeight}px`,
						}}
					>
						<canvas
							ref={fieldRef}
							className="card--field__canvas"
						/>

						{[
							{
								players:
									props.store.teamLineups?.homeTeamPlayers,
								positions: homePositions,
								team: "home",
								formation:
									props.store.teamLineups?.homeFormation,
							},
							{
								players:
									props.store.teamLineups?.awayTeamPlayers,
								positions: awayPositions,
								team: "away",
								formation:
									props.store.teamLineups?.awayFormation,
							},
						].map(({ players, positions, team, formation }) =>
							players.map((player, index) => {
								const position = positions[index];
								if (!position) return null;

								return (
									<TeamLineupsPlayerInfo
										key={`${team}-${index}`}
										store={props.store}
										team={team}
										position={position}
										player={player}
										index={index}
										formation={formation!}
										fieldDimensions={fieldDimensions}
										playerCount={players.length}
									/>
								);
							})
						)}
					</div>

					<div className="card--stats__row u-display--f--align-center">
						<div className="card--stats__col card--stats__col--left card--stats__col--grow">
							<div className="card--stats__subtitle u-type--sml">
								{
									props.store.playerStatisticsStore.eventStore
										.teamTwo?.name
								}
							</div>
						</div>
						<div className="card--stats__col card--stats__col--right u-display--f--align-center">
							<div className="card--stats__value card--stats__value--fit u-type--sml">
								{props.store.teamLineups?.awayFormation}
							</div>
							<TeamIcon
								showCountryFlag={
									props.store.playerStatisticsStore.eventStore
										.tournament?.showCountryFlag
								}
								flagCode={
									props.store.playerStatisticsStore.eventStore
										.teamTwo?.countryCode
								}
								jerseyTypeAbrv={
									props.store.playerStatisticsStore.eventStore
										.teamTwo?.jerseyTypeAbrv
								}
								opponentFlagCode={props.store.playerStatisticsStore.eventStore.teamOne?.countryCode}
								opponentJerseyTypeAbrv={props.store.playerStatisticsStore.eventStore.teamOne?.jerseyTypeAbrv}
								className="u-icon u-icon--med u-mar--left--3"
							/>
						</div>
					</div>
				</div>
			</div>
            <TeamLineupsBench store={props.store} />
		</>
	);
});
